import React, { useCallback, useMemo } from "react";

import { faNoteSticky } from "@fortawesome/pro-solid-svg-icons";
import { faCheckDouble } from "@fortawesome/pro-solid-svg-icons/faCheckDouble";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import {
  Company,
  Deal,
  EntitySupportingCard,
  EntityType,
  Person,
} from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";

import { ActionBarItemType } from "@app/component/ActionsBar";
import { showGlobalCreateActivityModal } from "@app/component/createEditEntity/Activity/store/actions";
import FrequencyIcon from "@app/component/frequency/FrequencyIcon";
import { initFrequencyModal } from "@app/store/frequency/actions";
import { isReadOnly } from "@app/store/iam/util";
import useClickTrackerContext from "@app/util/contexts/useClickTrackerContext";

import Actions from "./Actions";
import messages from "./messages";

const useCommonActions = (
  currentUser: User,
  entity: EntitySupportingCard,
  handleQuickActionButtonClick: (info: MenuInfo) => void,
  onEdit: (() => void) | undefined,
  onInitFrequencyModal: typeof initFrequencyModal.request,
  onShowCreateActivityModal: typeof showGlobalCreateActivityModal
) => {
  const intl = useIntl();
  const clickTrackerContext = useClickTrackerContext();
  const isReadOnlyMember = isReadOnly(currentUser);

  const handleManageFrequencies = useCallback(() => {
    clickTrackerContext.click(Actions.MANAGE_FREQUENCIES);
    onInitFrequencyModal({ entity });
  }, [clickTrackerContext, entity, onInitFrequencyModal]);

  const handleCreateActivity = useCallback(() => {
    clickTrackerContext.click(Actions.ADD_ACTIVITY);
    let account: Company | undefined;
    let contact: Person | undefined;
    let deal: Deal | undefined;

    if (entity.entity === EntityType.COMPANY) {
      account = entity as Company;
    } else if (entity.entity === EntityType.PERSON) {
      contact = entity as Person;
      account = (contact.primaryAccount ?? contact.accounts[0]) as Company | undefined;
    } else if (entity.entity === EntityType.DEAL) {
      deal = entity as Deal;
      contact = deal.contact;
      account = deal.account;
    }
    const now = new Date();

    onShowCreateActivityModal({
      activity: {
        account,
        contact,
        deal,
        startAt: now.toISOString(),
      },
    });
  }, [clickTrackerContext, entity, onShowCreateActivityModal]);

  const commonActions = useMemo(() => {
    const actions: ActionBarItemType[] = [
      {
        disabled: isReadOnlyMember,
        icon: <FontAwesomeIcon icon={faNoteSticky} />,
        key: Actions.ADD_NOTE,
        label: intl.formatMessage(messages.addNote),
        onClick: handleQuickActionButtonClick,
      },
      {
        disabled: isReadOnlyMember,
        icon: <FontAwesomeIcon icon={faCheckDouble} />,
        key: Actions.ADD_ACTIVITY,
        label: intl.formatMessage(messages.addActivity),
        onClick: handleCreateActivity,
      },
      {
        disabled: isReadOnlyMember,
        icon: <FrequencyIcon size="tiny" />,
        key: Actions.MANAGE_FREQUENCIES,
        label: intl.formatMessage(messages.manageFrequencies),
        onClick: handleManageFrequencies,
      },
    ];
    if (onEdit) {
      actions.push({
        disabled: isReadOnlyMember,
        icon: <FontAwesomeIcon icon={faPen} />,
        key: Actions.EDIT_ENTITY,
        label: intl.formatMessage(messages.edit),
        onClick: onEdit,
      });
    }
    return actions;
  }, [
    handleCreateActivity,
    handleManageFrequencies,
    handleQuickActionButtonClick,
    intl,
    isReadOnlyMember,
    onEdit,
  ]);

  return commonActions;
};

export default useCommonActions;
